import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, Power } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

const fullAddress = (b) =>
  [b.address_line1, b.address_line2, b.city, b.state, b.pin_code].filter(Boolean).join(", ") || b.address || "—";

export function AdminEstablishmentsList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => Promise.all([api.get("/admin/establishments"), api.get("/organisation")])
    .then(([e, o]) => { setRows(e.data); setOrgs(o.data); })
    .catch(e => toast.error(e.response?.data?.detail || "Load failed"));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(b => {
    if (orgFilter !== "all" && b.org_id !== orgFilter) return false;
    if (statusFilter === "active" && !b.is_active) return false;
    if (statusFilter === "inactive" && b.is_active) return false;
    return true;
  }), [rows, orgFilter, statusFilter]);
  const pg = usePagination(filtered, statusFilter);

  const toggle = async (b) => {
    try {
      const { data } = await api.post(`/admin/establishments/${b.id}/toggle-active`);
      toast.success(data.is_active ? `${b.name} activated` : `${b.name} deactivated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (b) => {
    if (!confirm(`Delete establishment "${b.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/establishments/${b.id}`); toast.success("Establishment deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="estmst-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Establishment Master</h1>
          <p className="text-muted-foreground">All establishments across every organisation — managed by the SaaS admin.</p>
        </div>
        <Button size="sm" onClick={() => nav("/admin/establishments/new")} data-testid="estmst-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New Establishment
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Organisation</Label>
              <Select value={orgFilter} onValueChange={setOrgFilter}>
                <SelectTrigger data-testid="estmst-filter-org" className="mt-1 w-64"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="estmst-org-all" value="all">All Organisations</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} data-testid={`estmst-org-${o.id}`} value={o.id}>{o.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="estmst-filter-status" className="mt-1 w-40"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="estmst-status-all" value="all">All Status</SelectItem>
                  <SelectItem data-testid="estmst-status-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="estmst-status-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="ml-auto" data-testid="estmst-count">{filtered.length} of {rows.length} shown</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="estmst-table">
            <TableHeader>
              <TableRow>
                <TableHead>Establishment Name</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map(b => (
                <TableRow key={b.id} data-testid={`estmst-row-${b.id}`}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="text-sm">{b.org_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-64 truncate">{fullAddress(b)}</TableCell>
                  <TableCell className="font-mono text-xs">{b.gstin || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{b.mobile || b.phone || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={b.is_active ? "default" : "destructive"} data-testid={`estmst-status-badge-${b.id}`}>
                      {b.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/admin/establishments/${b.id}/edit`)} data-testid={`estmst-edit-${b.id}`}>
                      <Pencil className="w-3.5 h-3.5"/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(b)} data-testid={`estmst-toggle-${b.id}`}
                      title={b.is_active ? "Deactivate" : "Activate"}>
                      <Power className={`w-3.5 h-3.5 ${b.is_active ? "text-destructive" : "text-primary"}`}/>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(b)} data-testid={`estmst-del-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8" data-testid="estmst-empty">
                  {rows.length ? "No establishments match the current filters." : "No establishments yet"}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <PaginationBar pg={pg}/>
        </CardContent>
      </Card>
    </div>
  );
}

const emptyForm = {
  org_id: "", name: "", address_line1: "", address_line2: "", city: "", state: "",
  pin_code: "", gstin: "", pan: "", mobile: "", email: "", is_active: true,
};

export function AdminEstablishmentForm() {
  const { bid } = useParams();
  const isEdit = !!bid;
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/organisation").then(({ data: orgList }) => {
      setOrgs(orgList);
      if (!isEdit) { setLoading(false); return; }
      api.get("/admin/establishments").then(({ data: rows }) => {
        const b = rows.find(x => x.id === bid);
        if (!b) { toast.error("Establishment not found"); nav("/admin/establishments"); return; }
        setF({
          org_id: b.org_id || "", name: b.name || "", address_line1: b.address_line1 || "",
          address_line2: b.address_line2 || "", city: b.city || "", state: b.state || "",
          pin_code: b.pin_code || "", gstin: b.gstin || "", pan: b.pan || "",
          mobile: b.mobile || b.phone || "", email: b.email || "", is_active: b.is_active !== false,
        });
        setLoading(false);
      });
    }).catch(() => { toast.error("Failed to load"); nav("/admin/establishments"); });
  }, [bid]);

  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e?.preventDefault();
    if (!f.org_id) { toast.error("Select an organisation"); return; }
    if (!f.name.trim()) { toast.error("Establishment name required"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/establishments/${bid}`, f);
      else await api.post("/admin/establishments", f);
      toast.success(isEdit ? "Establishment updated" : "Establishment created");
      nav("/admin/establishments");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="estmst-form-page">
      <Link to="/admin/establishments" data-testid="estmst-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="w-3 h-3"/> Back to Establishment Master
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEdit ? "Edit Establishment" : "New Establishment"}</CardTitle>
          <CardDescription>Changes reflect immediately on the organisation's Establishment page.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5" data-testid="estmst-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organisation *</Label>
                <Select value={f.org_id} onValueChange={(v) => setF(prev => ({ ...prev, org_id: v }))}>
                  <SelectTrigger data-testid="estmst-org" className="mt-1"><SelectValue placeholder="Select organisation"/></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} data-testid={`estmst-org-opt-${o.id}`} value={o.id}>{o.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Establishment Name *</Label>
                <Input data-testid="estmst-name" value={f.name} onChange={set("name")} className="mt-1" placeholder="e.g. Main Workshop"/>
              </div>
              <div>
                <Label>Address Line 1</Label>
                <Input data-testid="estmst-addr1" value={f.address_line1} onChange={set("address_line1")} className="mt-1"/>
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input data-testid="estmst-addr2" value={f.address_line2} onChange={set("address_line2")} className="mt-1"/>
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="estmst-city" value={f.city} onChange={set("city")} className="mt-1"/>
              </div>
              <div>
                <Label>State</Label>
                <Input data-testid="estmst-state" value={f.state} onChange={set("state")} className="mt-1"/>
              </div>
              <div>
                <Label>PIN Code</Label>
                <Input data-testid="estmst-pin" value={f.pin_code} onChange={set("pin_code")} className="mt-1 font-mono"/>
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input data-testid="estmst-gstin" value={f.gstin} onChange={set("gstin")} className="mt-1 font-mono uppercase"/>
              </div>
              <div>
                <Label>PAN</Label>
                <Input data-testid="estmst-pan" value={f.pan} onChange={set("pan")} className="mt-1 font-mono uppercase"/>
              </div>
              <div>
                <Label>Mobile</Label>
                <Input data-testid="estmst-mobile" value={f.mobile} onChange={set("mobile")} className="mt-1 font-mono"/>
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="estmst-email" type="email" value={f.email} onChange={set("email")} className="mt-1"/>
              </div>
              <div className="flex items-end justify-between border border-border rounded-sm px-3 py-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Active</Label>
                <Switch data-testid="estmst-status" checked={f.is_active}
                  onCheckedChange={(v) => setF(prev => ({ ...prev, is_active: v }))}/>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="estmst-save" type="submit" disabled={busy}>
                {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Establishment"}
              </Button>
              <Button data-testid="estmst-cancel" type="button" variant="outline" onClick={() => nav("/admin/establishments")}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
